import { useMemo, useState } from "react";

import { PRO014_STORE_MODE } from "@/@types/campaign";
import type {
  IPro014CampaignDetail,
  IUpdatePro014StoresResponse,
  Pro014StoreMode,
  StoreSelectorValue,
} from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { useToast } from "@/components/ui/use-toast";
import {
  useGetPro014CampaignByCodeQuery,
  useUpdatePro014CampaignStoresMutation,
} from "@/services/campaign-pro014";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { PRO014_STORES_TEXTS } from "../components/edit-campaign/constants";
import type { Pro014StoresEditorState, Pro014StoresPreview } from "../types";
import { MESSAGES, NO_CHANGES_TOAST } from "../utils/messages";
import {
  conflictsFromError,
  describeApiError,
  isConcurrentChangeError,
} from "../utils/pro014-api-errors";
import {
  buildPro014StoresPayload,
  buildPro014StoresPreview,
  describePro014StoresUpdate,
  initialPro014StoresSelection,
  isPro014StoresSelectionValid,
  isQueuedStoresUpdate,
  normalizeStoreSelection,
} from "../utils/pro014-stores-diff";
import { useAllStoreCodes } from "./use-all-store-codes";

const sameSelection = (left: StoreSelectorValue, right: StoreSelectorValue): boolean => {
  const rightStores: Set<number> = new Set(right.stores);
  return (
    left.allStores === right.allStores &&
    left.stores.length === right.stores.length &&
    left.stores.every((store: number) => rightStores.has(store))
  );
};

const effectiveStoreSelection = (
  storeMode: Pro014StoreMode,
  selection: StoreSelectorValue,
  initial: StoreSelectorValue,
  allStoreCodes: number[],
): StoreSelectorValue => {
  if (storeMode === PRO014_STORE_MODE.LEGACY || sameSelection(selection, initial)) {
    return selection;
  }
  return normalizeStoreSelection(selection, allStoreCodes);
};

export function usePro014StoresEditor(
  campaignCode: number,
  detail: IPro014CampaignDetail,
  onPendingChange?: (pending: boolean) => void,
): Pro014StoresEditorState {
  const { toast } = useToast();
  const [updateStores, { isLoading: isSaving }] = useUpdatePro014CampaignStoresMutation();
  const { refetch: refetchDetail } = useGetPro014CampaignByCodeQuery(campaignCode);
  const [initialSelection] = useState<StoreSelectorValue>(() =>
    initialPro014StoresSelection(detail),
  );
  const [selection, setSelection] = useState<StoreSelectorValue>(initialSelection);
  const [error, setError] = useState<string>("");
  const [isConfirmingAll, setIsConfirmingAll] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [isQueued, setIsQueued] = useState<boolean>(false);
  const allStoreCodes: number[] = useAllStoreCodes();

  const effectiveSelection: StoreSelectorValue = useMemo(
    () => effectiveStoreSelection(detail.storeMode, selection, initialSelection, allStoreCodes),
    [detail.storeMode, selection, initialSelection, allStoreCodes],
  );

  const preview: Pro014StoresPreview = useMemo(
    () => buildPro014StoresPreview(detail.storeMode, detail.storeWindows, effectiveSelection),
    [detail.storeMode, detail.storeWindows, effectiveSelection],
  );

  const updateSelection = (changes: Partial<StoreSelectorValue>): void => {
    const next: StoreSelectorValue = { ...selection, ...changes };
    setSelection(next);
    setError("");
    setIsConfirmingAll(false);
    onPendingChange?.(
      buildPro014StoresPreview(
        detail.storeMode,
        detail.storeWindows,
        effectiveStoreSelection(detail.storeMode, next, initialSelection, allStoreCodes),
      ).hasChanges,
    );
  };

  const handleSaveError = async (saveError: unknown): Promise<void> => {
    const found: CampaignConflictItem[] = conflictsFromError(saveError);
    if (found.length > 0) {
      setConflicts(found);
      return;
    }
    if (isConcurrentChangeError(saveError)) {
      toast({
        ...MESSAGES.UPDATE_STORES.ERROR,
        description: extractRtkErrorMessage(saveError, PRO014_STORES_TEXTS.CHANGED_ELSEWHERE),
      });
      onPendingChange?.(false);
      await refetchDetail();
      return;
    }
    toast({
      ...MESSAGES.UPDATE_STORES.ERROR,
      description: describeApiError(saveError, MESSAGES.UPDATE_STORES.ERROR.description),
    });
  };

  const persist = async (): Promise<void> => {
    const response: IUpdatePro014StoresResponse | null = await updateStores({
      code: campaignCode,
      ...buildPro014StoresPayload(effectiveSelection),
    })
      .unwrap()
      .catch(async (saveError: unknown) => {
        await handleSaveError(saveError);
        return null;
      });
    if (!response) return;
    setIsQueued(isQueuedStoresUpdate(response));
    onPendingChange?.(false);
    toast({ ...MESSAGES.UPDATE_STORES.SUCCESS, description: describePro014StoresUpdate(response) });
  };

  const requestSave = async (): Promise<void> => {
    if (isQueued) return;
    if (!isPro014StoresSelectionValid(effectiveSelection)) {
      setError(PRO014_STORES_TEXTS.SELECT_AT_LEAST_ONE);
      return;
    }
    if (!preview.hasChanges) {
      toast(NO_CHANGES_TOAST);
      return;
    }
    if (preview.switchesToAll) {
      setIsConfirmingAll(true);
      return;
    }
    await persist();
  };

  const confirmSwitchToAll = async (): Promise<void> => {
    setIsConfirmingAll(false);
    await persist();
  };

  return {
    selection,
    preview,
    error,
    isConfirmingAll,
    isSaving,
    isQueued,
    conflicts,
    updateSelection,
    clearError: () => setError(""),
    requestSave,
    confirmSwitchToAll,
    cancelSwitchToAll: () => setIsConfirmingAll(false),
    closeConflicts: () => setConflicts([]),
  };
}
